import { faStickyNote, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext } from 'react'
import z from 'zod'
import { useNewNoteMutation } from '~/api/mutations/action'
import {
  Dropdown,
  Form,
  InputCol,
  Submit,
  TextareaInput
} from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { AsideActionsItems } from './AsideActionsItems'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  notes: z.string().min(1, 'Veuillez renseigner une note'),
  actionType: z.string().optional()
})

type FormValues = z.infer<typeof formSchema>

type NoteActionProps = {
  applicantIds?: number[]
  contactIds?: number[]
  orderContext?: {
    id: number
    willChangeStatus: boolean
  }
}

export const NoteAction = ({
  applicantIds = [],
  contactIds = [],
  orderContext
}: NoteActionProps) => {
  const noteCreationMutation = useNewNoteMutation()
  const { setIsDirty } = useContext(AsideActionContext)
  const closeAction = useCloseAction()
  const { data: actionTypes } = useTaxonomy('action_types', {
    context:
      contactIds.length > 0 && applicantIds.length === 0
        ? 'contact'
        : 'applicant'
  })
  const classifiableActionTypes = actionTypes?.filter(
    (type) => !type.expectsCompletion
  )

  const onSubmit = async (values: FormValues) =>
    noteCreationMutation.mutateAsync(
      {
        applicants: applicantIds,
        contacts: contactIds,
        notes: values.notes,
        actionType: values.actionType ?? undefined,
        orderId: orderContext?.id
      },
      {
        onSuccess: () => {
          closeAction(true)
          toast(
            'Note enregistrée',
            'La note a bien été enregistrée',
            ToastType.Success
          )
          if (typeof window.paginatorHistoriqueActions !== 'undefined') {
            window.paginatorHistoriqueActions.refresh()
          }
        }
      }
    )

  return (
    <Form onSubmit={onSubmit} schema={formSchema}>
      <AsideAction
        title="Note"
        icon={faStickyNote}
        applicantIds={applicantIds.length > 0 ? applicantIds : undefined}
        contactIds={
          applicantIds.length === 0 && contactIds.length > 0
            ? contactIds
            : undefined
        }
      >
        <AsideActionsItems>
          <Button
            variation={ButtonVariation.DangerInversed}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              closeAction()
            }}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Ajout..."
            successText="Ajouté !"
            onSuccessReset={false}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faStickyNote} />
            Ajouter
          </Submit>
        </AsideActionsItems>
        <InputCol>
          {(classifiableActionTypes?.length ?? 0) > 0 && (
            <Dropdown
              label=""
              name="actionType"
              placeholder="Classer"
              options={
                classifiableActionTypes?.map((type) => ({
                  label: type.name,
                  value: String(type.id)
                })) || []
              }
            />
          )}
          <TextareaInput
            label=""
            name="notes"
            placeholder="Saisissez votre note..."
            onChange={() => setIsDirty(true)}
          />
        </InputCol>
      </AsideAction>
    </Form>
  )
}
